import { useEffect } from 'react';
import type { ReactNode } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Route,
  Navigate,
  Outlet,
  useSearchParams,
  useNavigate,
} from 'react-router-dom';
import { safeRedirectPath, SSO_RETURN_TO_KEY } from '@/lib/safeRedirect';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { RequireAdmin } from '@/components/RequireAdmin';
import { Layout } from '@/components/Layout';
import { Toaster } from '@/components/ui/toaster';
import { useAuthStore } from '@/store/auth';
import { Login } from '@/pages/Login';
import { SetPassword } from '@/pages/SetPassword';
import { ForgotPassword } from '@/pages/ForgotPassword';
import { ResetPassword } from '@/pages/ResetPassword';
import { Dashboard } from '@/pages/Dashboard';
import { Sources } from '@/pages/Sources';
import { Datasets } from '@/pages/Datasets';
import { DatasetDetails } from '@/pages/DatasetDetails';
import { DatasetCreate } from '@/pages/DatasetCreate';
import { Ingestion } from '@/pages/Ingestion';
import { Catalog } from '@/pages/Catalog';
import { Quality } from '@/pages/Quality';
import { UserManagement } from '@/pages/UserManagement';
import { RoleManagement } from '@/pages/RoleManagement';
import { UploadPage } from '@/pages/UploadPage';
import { SchemaLibraryPage } from '@/pages/SchemaLibraryPage';
import { SchemaCreatePage } from '@/pages/SchemaCreatePage';
import { MonitorListPage } from '@/pages/MonitorListPage';
import { MonitorCreatePage } from '@/pages/MonitorCreatePage';
import { MonitorDetailsPage } from '@/pages/MonitorDetailsPage';
import { MonitorEditPage } from '@/pages/MonitorEditPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { LineagePage } from '@/pages/LineagePage';
import { ViewListPage } from '@/pages/ViewListPage';
import { ViewCreatePage } from '@/pages/ViewCreatePage';
import { ViewDetailsPage } from '@/pages/ViewDetailsPage';
import { ViewEditPage } from '@/pages/ViewEditPage';
import { ConnectorsPage } from '@/pages/ConnectorsPage';
import { ConnectorStatisticsPage } from '@/pages/ConnectorStatisticsPage';
import { ScheduleTimelinePage } from '@/pages/ScheduleTimelinePage';
import { ConnectorDetailPage } from '@/pages/ConnectorDetailPage';
import { ConnectorCreatePage } from '@/pages/ConnectorCreatePage';
import { ExecutionMonitoringTest } from '@/components/connectors/monitoring/ExecutionMonitoringTest';
import { ExecutionNotificationsProvider } from '@/components/connectors/ExecutionNotificationsProvider';

/**
 * RootLayout holds cross-cutting UI (toaster, execution-notification socket) and
 * the one-time auth-init effect, then renders the matched route via <Outlet/>.
 * It is the element of the pathless root route so these stay mounted across
 * navigations (matching the pre-data-router behavior). Migrated to a data router
 * (#303) so the connector wizard can use useBlocker for full in-app nav blocking.
 */
export function RootLayout() {
  const initializeAuth = useAuthStore((s) => s.initializeAuth);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    // One-time migration: remove legacy localStorage keys
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth-storage');
    initializeAuth();
  }, [initializeAuth]);

  // On the SSO landing (?sso=success), restore the route the operator attempted
  // before the full-page Microsoft redirect (stashed by Login, #1124). With no
  // stashed route, fall back to stripping the marker so it does not linger (#569).
  useEffect(() => {
    if (searchParams.get('sso') !== 'success') return;

    let stashed: { pathname?: string; search?: string; hash?: string } | null = null;
    try {
      const raw = sessionStorage.getItem(SSO_RETURN_TO_KEY);
      sessionStorage.removeItem(SSO_RETURN_TO_KEY);
      stashed = raw ? JSON.parse(raw) : null;
    } catch {
      stashed = null;
    }

    if (stashed && typeof stashed === 'object') {
      // Path navigation both restores the route and drops the ?sso=success marker.
      navigate(safeRedirectPath(stashed), { replace: true });
      return;
    }

    const next = new URLSearchParams(searchParams);
    next.delete('sso');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, navigate]);

  return (
    <>
      <Toaster />
      <ExecutionNotificationsProvider />
      <Outlet />
    </>
  );
}

const protect = (el: ReactNode) => (
  <ProtectedRoute>
    <Layout>{el}</Layout>
  </ProtectedRoute>
);

// Admin-only routes (gated server-side by `*:*`): non-superusers see a clean
// access-denied state inside the Layout instead of the page's 403 error (#353).
const protectAdmin = (el: ReactNode) => (
  <ProtectedRoute>
    <Layout>
      <RequireAdmin>{el}</RequireAdmin>
    </Layout>
  </ProtectedRoute>
);

export const appRoutes = createRoutesFromElements(
  <Route element={<RootLayout />}>
    {/* Public Routes */}
    <Route path="/login" element={<Login />} />
    <Route path="/set-password" element={<SetPassword />} />
    <Route path="/forgot-password" element={<ForgotPassword />} />
    <Route path="/reset-password" element={<ResetPassword />} />

    {/* Protected Routes */}
    <Route path="/dashboard" element={protect(<Dashboard />)} />
    <Route path="/sources" element={protect(<Sources />)} />
    <Route path="/datasets" element={protect(<Datasets />)} />
    <Route path="/datasets/:id" element={protect(<DatasetDetails />)} />
    <Route path="/datasets/create" element={protect(<DatasetCreate />)} />
    <Route path="/ingestion" element={protect(<Ingestion />)} />
    <Route path="/catalog" element={protect(<Catalog />)} />
    <Route path="/quality" element={protect(<Quality />)} />
    <Route path="/users" element={protectAdmin(<UserManagement />)} />
    <Route path="/roles" element={protectAdmin(<RoleManagement />)} />
    <Route path="/upload" element={protect(<UploadPage />)} />
    <Route path="/schemas" element={protect(<SchemaLibraryPage />)} />
    <Route path="/schemas/new" element={protect(<SchemaCreatePage />)} />
    <Route path="/monitors" element={protect(<MonitorListPage />)} />
    <Route path="/monitors/new" element={protect(<MonitorCreatePage />)} />
    <Route path="/monitors/:id" element={protect(<MonitorDetailsPage />)} />
    <Route path="/monitors/:id/edit" element={protect(<MonitorEditPage />)} />
    <Route path="/upload-history" element={<Navigate to="/dashboard" replace />} />
    <Route path="/settings" element={protect(<SettingsPage />)} />
    <Route path="/lineage" element={protect(<LineagePage />)} />
    <Route path="/query" element={<Navigate to="/dashboard" replace />} />
    <Route path="/views" element={protect(<ViewListPage />)} />
    <Route path="/views/new" element={protect(<ViewCreatePage />)} />
    <Route path="/views/:id" element={protect(<ViewDetailsPage />)} />
    <Route path="/views/:id/edit" element={protect(<ViewEditPage />)} />
    <Route path="/connectors/statistics" element={protect(<ConnectorStatisticsPage />)} />
    {/* Literal segments must precede /connectors/:connectorType/:name, or the
        param route swallows them (#946). */}
    <Route path="/connectors/schedule" element={protect(<ScheduleTimelinePage />)} />
    <Route path="/connectors/new" element={protect(<ConnectorCreatePage />)} />
    <Route path="/connectors/:connectorType/:name" element={protect(<ConnectorDetailPage />)} />
    <Route path="/connectors" element={protect(<ConnectorsPage />)} />

    {/* Test route for Phase 1 monitoring - TEMPORARY */}
    <Route path="/test/execution-monitoring" element={protect(<ExecutionMonitoringTest />)} />

    {/* Redirects */}
    <Route path="/" element={<Navigate to="/dashboard" replace />} />
    <Route path="*" element={<Navigate to="/dashboard" replace />} />
  </Route>,
);

const router = createBrowserRouter(appRoutes);

function App() {
  return <RouterProvider router={router} />;
}

export default App;
